import { IconButton, useMediaQuery } from "@chakra-ui/react"
import { BlockText, List, QuillEditor } from "@pathwright/blocks/src/shared"
import { Content } from "@pathwright/ui/src/components/content/Content"
import { TOOLBAR_CONFIG_TYPES } from "@pathwright/ui/src/components/quill/toolbar/QuillToolbar"
import Pathicon from "@pathwright/ux/src/components/pathicon/Pathicon"
import {
  type ComponentType,
  type CSSProperties,
  type ReactNode,
  type TouchEvent,
  useCallback,
  useEffect,
  useMemo,
  useState
} from "react"
import QuestionHeader from "../Question/QuestionHeader"
import { QuestionContainer } from "../Question/ui"
import { StatementMenu } from "./controls"
import { MobileScaleSummary, type ScaleProps } from "./scale"
import { DesktopStatement, MobileStatement } from "./statement"
import type {
  LikertScaleBlockProps,
  LikertScaleEditorStatement,
  ListDragHandleComponent,
  StatementListRenderProps,
  StatementListUpdate
} from "./types"
import {
  AddStatementButton,
  AddStatementReveal,
  DesktopStatementActions,
  DragHandle,
  LIKERT_MOBILE_BREAKPOINT,
  StatementEditorMain,
  StatementListContainer
} from "./ui"
import {
  buildEditorItems,
  createLikertStatement,
  getEditableLikertScale,
  getLikertStatements,
  getNewItemIndices,
  hasVisibleText,
  updateOrdering
} from "./utils"

const BlocksList = List as ComponentType<any>
const BlocksContentContainer = Content.Container as ComponentType<{
  children: ReactNode
  style?: CSSProperties
  flexForPrint?: boolean
}>
const LikertQuestionContainer = QuestionContainer as ComponentType<{
  children: ReactNode
  blockStyles?: LikertScaleBlockProps["blockStyles"]
}>
const LikertQuestionHeader = QuestionHeader as ComponentType<{
  data: LikertScaleBlockProps["data"]
  onChange?: LikertScaleBlockProps["onChange"]
  questionField?: string
  descriptionStyle?: { edit?: CSSProperties }
  preview?: boolean
}>
type StatementActions = {
  canDelete: boolean
  canMoveUp: boolean
  canMoveDown: boolean
  onDelete: () => void
  onDuplicate: () => void
  onMoveUp: () => void
  onMoveDown: () => void
}

const StatementDragMenu = ({
  DragHandle: ListDragHandle,
  ...actions
}: StatementActions & { DragHandle: ListDragHandleComponent }) => (
  <ListDragHandle>
    {(dragHandleProps = {}) => (
      <StatementMenu
        {...actions}
        canOpen={dragHandleProps.canOpen}
        trigger={
          <DragHandle
            aria-label="Drag to reorder or open statement actions"
            role="button"
            tabIndex={0}
            onPointerDown={dragHandleProps.onPointerDown}
            onKeyDown={(event) => {
              if (event.key === "Enter" || event.key === " ") {
                event.preventDefault()
                event.currentTarget.click()
              }
            }}
          >
            <Pathicon icon="drag" />
          </DragHandle>
        }
      />
    )}
  </ListDragHandle>
)

// Prevent step Carousel (react-swipeable) from claiming touches inside this block.
const stopCarouselSwipe = (event: TouchEvent<HTMLDivElement>) =>
  event.stopPropagation()

const getMovedItems = (
  items: LikertScaleEditorStatement[],
  index: number,
  nextIndex: number
): LikertScaleEditorStatement[] => {
  if (nextIndex < 0 || nextIndex >= items.length) return items
  const nextItems = [...items]
  const [item] = nextItems.splice(index, 1)
  nextItems.splice(nextIndex, 0, item)
  return updateOrdering(nextItems)
}

const LikertScaleEdit = ({
  data,
  onChange,
  blockStyles,
  id
}: LikertScaleBlockProps) => {
  const [isMobile] = useMediaQuery([
    `(max-width: ${LIKERT_MOBILE_BREAKPOINT}px)`
  ])
  const [expandedStatementID, setExpandedStatementID] = useState<string | null>(
    null
  )
  const [editingStatementID, setEditingStatementID] = useState<string | null>(
    null
  )
  const [addingStatementID, setAddingStatementID] = useState<string | null>(
    null
  )
  const [openScaleEditorStatementID, setOpenScaleEditorStatementID] = useState<
    string | null
  >(null)
  const [focusStatementID, setFocusStatementID] = useState<string | null>(null)
  useEffect(() => {
    const editorWillBeRendered =
      !isMobile ||
      expandedStatementID === focusStatementID ||
      addingStatementID === focusStatementID ||
      editingStatementID === focusStatementID

    if (!focusStatementID || !editorWillBeRendered) return

    const frame = requestAnimationFrame(() => {
      const editor = document.querySelector<HTMLElement>(
        `[data-likert-statement="${focusStatementID}"] .ql-editor`
      )
      editor?.focus()

      // Persisting the first character replaces the draft statement tree.
      // Restore the caret after the new Quill instance mounts so subsequent
      // typing continues naturally instead of starting before that character.
      if (editor) {
        const range = document.createRange()
        const selection = window.getSelection()
        range.selectNodeContents(editor)
        range.collapse(false)
        selection?.removeAllRanges()
        selection?.addRange(range)
      }

      setFocusStatementID(null)
    })
    return () => cancelAnimationFrame(frame)
  }, [
    addingStatementID,
    editingStatementID,
    expandedStatementID,
    focusStatementID,
    isMobile
  ])

  const scale = useMemo(() => getEditableLikertScale(data.scale), [data.scale])
  const showNumbers = data.showNumbers !== false
  const statements = useMemo(
    () => getLikertStatements(data.statements),
    [data.statements]
  )
  const statementItems = useMemo(
    () =>
      buildEditorItems({
        items: data.statements,
        min: 1,
        createItem: createLikertStatement,
        getText: (item) => item.label,
        idField: "statementID"
      }),
    [data.statements]
  )
  useEffect(() => {
    if (
      addingStatementID &&
      !statementItems.some(
        (item) => item.isNew && item.statementID === addingStatementID
      )
    ) {
      setAddingStatementID(null)
    }
  }, [addingStatementID, statementItems])
  const handleStatementChange = useCallback(
    ({ items }: { items: LikertScaleEditorStatement[] }) =>
      onChange({ statements: getLikertStatements(items) }),
    [onChange]
  )

  const updateLabel = (onUpdate: StatementListUpdate, label: string) => {
    onUpdate({
      updateFunction: ({ items, index }) => {
        const nextItems = [...items]
        const nextItem = { ...items[index], label }
        if (nextItem.isNew && hasVisibleText(label)) delete nextItem.isNew
        nextItems[index] = nextItem
        return { items: nextItems }
      }
    })
  }

  const updateStatementItems = (
    onUpdate: StatementListUpdate,
    transform: (
      items: LikertScaleEditorStatement[],
      index: number
    ) => LikertScaleEditorStatement[]
  ) => {
    onUpdate({
      updateFunction: ({ items, index }) => ({
        items: transform([...items], index)
      })
    })
  }

  const duplicateStatement = (onUpdate: StatementListUpdate) => {
    updateStatementItems(onUpdate, (items, index) => {
      const item = items[index]
      items.splice(index + 1, 0, {
        ...createLikertStatement({ label: item.label }),
        order: index + 2
      })
      return updateOrdering(items)
    })
  }

  const toggleExpanded = (statementID: string) => {
    setExpandedStatementID((current) =>
      current === statementID ? null : statementID
    )
  }

  const getScaleProps = (
    statementID: string
  ): Omit<ScaleProps, "statementLabel"> => ({
    scale,
    showNumbers,
    name: `likert-${id}-${statementID}-preview`,
    disabled: true,
    showEditTrigger: true,
    scaleEditorOpen: openScaleEditorStatementID === statementID,
    onScaleChange: (nextScale) => onChange({ scale: nextScale }),
    onScaleEditorOpenChange: (open) =>
      setOpenScaleEditorStatementID((current) =>
        open ? statementID : current === statementID ? null : current
      ),
    onShowNumbersChange: (nextShowNumbers) =>
      onChange({ showNumbers: nextShowNumbers })
  })

  const renderStatementEditor = ({
    item,
    onUpdate,
    desktop = false,
    bounds
  }: Pick<StatementListRenderProps, "item" | "onUpdate"> & {
    desktop?: boolean
    bounds?: string
  }) => (
    <StatementEditorMain $desktopStatement={desktop || undefined}>
      <QuillEditor
        toolbar={TOOLBAR_CONFIG_TYPES.SIMPLE}
        html={item.label}
        placeholder="Add a statement"
        onChange={(label) => {
          if (item.isNew && hasVisibleText(label)) {
            if (!desktop) setEditingStatementID(item.statementID)
            setFocusStatementID(item.statementID)
          }
          updateLabel(onUpdate, label)
        }}
        onBlur={
          desktop
            ? undefined
            : () => {
                setEditingStatementID((current) =>
                  current === item.statementID ? null : current
                )
              }
        }
        controlled
        autoListFormatting={false}
        {...(bounds ? { bounds } : {})}
      />
    </StatementEditorMain>
  )

  const getStatementActions = ({
    index,
    item,
    onRemove,
    onUpdate
  }: StatementListRenderProps): StatementActions => {
    const persistedCount = statementItems.filter(
      (statement) => !statement.isNew
    ).length
    return {
      canDelete: statements.length > 1 && !item.isNew,
      canMoveUp: index > 0 && !item.isNew,
      canMoveDown: index < persistedCount - 1 && !item.isNew,
      onDelete: onRemove,
      onDuplicate: () => duplicateStatement(onUpdate),
      onMoveUp: () =>
        updateStatementItems(onUpdate, (items, currentIndex) =>
          getMovedItems(items, currentIndex, currentIndex - 1)
        ),
      onMoveDown: () =>
        updateStatementItems(onUpdate, (items, currentIndex) =>
          getMovedItems(items, currentIndex, currentIndex + 1)
        )
    }
  }

  const renderMobileStatement = (props: StatementListRenderProps) => {
    const { item, index, DragHandle: ListDragHandle, onUpdate } = props
    if (item.isNew) return renderAddStatement(props, true)

    const actions = getStatementActions(props)
    const expanded = expandedStatementID === item.statementID
    const editing = expanded || editingStatementID === item.statementID

    return (
      <MobileStatement
        id={`${id}-statement-${index}`}
        statementID={item.statementID}
        statementLabel={item.label}
        expanded={expanded}
        leading={<StatementDragMenu DragHandle={ListDragHandle} {...actions} />}
        trailing={
          <IconButton
            aria-label={expanded ? "Collapse statement" : "Expand statement"}
            size="2xs"
            variant="ghost"
            onClick={() => toggleExpanded(item.statementID)}
          >
            <Pathicon icon={expanded ? "chevron-up" : "chevron-down"} />
          </IconButton>
        }
        onHeaderClick={() => {
          if (!editing) {
            setExpandedStatementID(item.statementID)
            setFocusStatementID(item.statementID)
          }
        }}
        scaleProps={getScaleProps(item.statementID)}
        collapsedSummary={
          !expanded ? (
            <MobileScaleSummary scale={scale} showNumbers={showNumbers} />
          ) : null
        }
      >
        {editing ? (
          renderStatementEditor({
            item,
            onUpdate,
            bounds: `[data-likert-statement="${item.statementID}"]`
          })
        ) : (
          <BlockText html={item.label} />
        )}
      </MobileStatement>
    )
  }

  const renderDesktopStatement = (props: StatementListRenderProps) => {
    const { item, index, DragHandle: ListDragHandle, onUpdate } = props
    if (item.isNew) return renderAddStatement(props, false)

    const actions = getStatementActions(props)

    return (
      <DesktopStatement
        id={`${id}-statement-${index}`}
        statementID={item.statementID}
        statementLabel={item.label}
        actions={
          <DesktopStatementActions className="LikertDesktopStatementActions">
            <ListDragHandle>
              {(dragHandleProps = {}) => (
                <DragHandle
                  aria-label="Drag to reorder statement"
                  role="button"
                  tabIndex={0}
                  onPointerDown={dragHandleProps.onPointerDown}
                >
                  <Pathicon icon="arrow-double-vertical-2" />
                </DragHandle>
              )}
            </ListDragHandle>
            <IconButton
              aria-label="Duplicate statement"
              size="2xs"
              variant="ghost"
              onClick={actions.onDuplicate}
            >
              <Pathicon icon="copy" />
            </IconButton>
            <IconButton
              aria-label="Delete statement"
              disabled={!actions.canDelete}
              size="2xs"
              variant="ghost"
              onClick={actions.onDelete}
            >
              <Pathicon icon="trash" />
            </IconButton>
          </DesktopStatementActions>
        }
        scaleProps={{
          ...getScaleProps(item.statementID),
          editMode: true
        }}
      >
        {renderStatementEditor({ item, onUpdate, desktop: true })}
      </DesktopStatement>
    )
  }

  const renderAddStatement = (
    props: StatementListRenderProps,
    mobile: boolean
  ) => {
    const { item, index, onUpdate } = props
    const expanded = addingStatementID === item.statementID
    const activate = () => {
      setAddingStatementID(item.statementID)
      setFocusStatementID(item.statementID)
    }
    const editor = mobile ? (
      <MobileStatement
        id={`${id}-statement-${index}`}
        statementID={item.statementID}
        statementLabel={item.label}
        expanded={false}
        scaleProps={getScaleProps(item.statementID)}
      >
        {renderStatementEditor({
          item,
          onUpdate,
          bounds: `[data-likert-statement="${item.statementID}"]`
        })}
      </MobileStatement>
    ) : (
      <DesktopStatement
        id={`${id}-statement-${index}`}
        statementID={item.statementID}
        statementLabel={item.label}
        draft
        scaleProps={{
          ...getScaleProps(item.statementID),
          editMode: true
        }}
      >
        {renderStatementEditor({ item, onUpdate, desktop: true })}
      </DesktopStatement>
    )

    return (
      <>
        {!expanded ? (
          <AddStatementButton type="button" onClick={activate}>
            <Pathicon icon="plus-circle" />
            <span style={{ marginBottom: "-2px", marginTop: "2px" }}>
              Add statement
            </span>
          </AddStatementButton>
        ) : null}
        <AddStatementReveal $expanded={expanded}>
          <div>{expanded ? editor : null}</div>
        </AddStatementReveal>
      </>
    )
  }

  return (
    <BlocksContentContainer>
      <LikertQuestionContainer blockStyles={blockStyles}>
        <LikertQuestionHeader
          data={data}
          onChange={onChange}
          questionField="prompt"
          descriptionStyle={{ edit: { padding: "2px 0" } }}
        />
        <StatementListContainer
          $mobile={isMobile}
          className="likert-scale-container"
          onTouchStart={stopCarouselSwipe}
        >
          <BlocksList
            direction="vertical"
            dragMode="motion"
            disabledIndices={getNewItemIndices(statementItems)}
            items={statementItems}
            min={1}
            renderItem={
              isMobile ? renderMobileStatement : renderDesktopStatement
            }
            onChange={handleStatementChange}
            id={`${id}-${isMobile ? "mobile" : "desktop"}-statements`}
            listStyles={{ width: "100%" }}
            itemContainerStyles={{
              display: "flex",
              flexDirection: "column"
            }}
          />
        </StatementListContainer>
      </LikertQuestionContainer>
    </BlocksContentContainer>
  )
}

export default LikertScaleEdit
